// "Observed so far · not yet scored" (DESIGN_SPEC §12.11; the live cockpit's fourth tab).
//   <ObservedList ctx rows total onSeeAll? />
//   up to eight rows (T0-relative time · category chip · what happened, ×N folded), chronological, no severities and no
//   technique ids; "See all N events" hands over to the Events tab.
//   <Parts parts />   inline text: id → mono .id (no fill), href → .link (in-page figures scroll smoothly), q → <q>,
//                     at → <time datetime>. Long identifiers may wrap so phones never scroll sideways.
import { Fragment, type MouseEvent } from "react";
import { ActionButton, CategoryChip, Empty } from "@/components/ui";
import { count, rel, utc } from "@/lib/format";
import type { ReportCtx } from "@/lib/derive/types";
import type { ObservedRow, Part } from "./observed";
import { scrollToId } from "./scroll";

function jump(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute("href") ?? "";
  if (href.startsWith("#") && scrollToId(href.slice(1))) e.preventDefault();
}

export function Parts({ parts }: { parts: readonly Part[] }) {
  return (
    <>
      {parts.map((p, i) => {
        if (p.href)
          return (
            <a key={i} className="link wrap-anywhere" href={p.href} onClick={jump} translate="no">
              {p.text}
            </a>
          );
        if (p.id)
          return (
            <span key={i} className={p.text.length > 24 ? "id whitespace-normal wrap-anywhere" : "id"} translate="no" spellCheck={false}>
              {p.text}
            </span>
          );
        if (p.q) return <q key={i}>{p.text}</q>;
        if (p.at !== undefined)
          return (
            <time key={i} dateTime={new Date(p.at).toISOString()} title={`${new Date(p.at).toISOString().replace("T", " ").replace("Z", "")} UTC`}>
              {p.text}
            </time>
          );
        return <Fragment key={i}>{p.text}</Fragment>;
      })}
    </>
  );
}

export function ObservedList({
  ctx,
  rows,
  total,
  onSeeAll,
}: {
  ctx: ReportCtx;
  rows: readonly ObservedRow[];
  total: number;
  onSeeAll?: () => void;
}) {
  const t0 = ctx.stream.t0;
  const n = ctx.stream.snap.events.length;
  return (
    <div className="min-w-0">
      {rows.length ? (
        <ul className="observed border-t border-rule-strong">
          {rows.map((r) => (
            <li key={r.key} className="grid-cols-[11.5ch_92px_minmax(0,1fr)] max-[720px]:grid-cols-[10.5ch_minmax(0,1fr)]">
              <span className="tm">{t0 !== null && Number.isFinite(r.ts) ? rel(r.ts - t0) : utc(r.ts, "time")}</span>
              <span className="mk max-[720px]:justify-self-start">
                <CategoryChip c={r.category} size="sm" />
              </span>
              <span className="say min-w-0 max-[720px]:col-span-2 max-[720px]:mt-1">
                <Parts parts={r.parts} />
                {r.count > 1 && <span className="ml-2 font-mono text-[11px] text-ink-faint tabular-nums">×{count(r.count)}</span>}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <Empty inline rule={false}>
          Nothing from the sample yet
        </Empty>
      )}
      {onSeeAll && n > 0 && (
        <p className="mt-3">
          <ActionButton onClick={onSeeAll}>
            {total > rows.length ? `${count(total - rows.length)} earlier · ` : ""}See all {count(n)} events
          </ActionButton>
        </p>
      )}
    </div>
  );
}
